import { withdrawalStatus } from '@kioskgaming/ui';

/**
 * PaymentTransaction withdrawal statuses — keep in sync with backend ENUM:
 * `payment_transactions.status` in PaymentTransaction model.
 */
export const WITHDRAWAL_PAYMENT_STATUSES = [
  'pending',
  'processing',
  'approved',
  'awaiting_admin',
  'retry_scheduled',
  'completed',
  'failed',
  'rejected',
  'cancelled',
  'expired',
  'partial',
  'refunded',
] as const;

export type WithdrawalPaymentStatus = (typeof WITHDRAWAL_PAYMENT_STATUSES)[number];

export type WithdrawalStatusDisplay = {
  status: string;
  label: string;
  className: string;
};

/** Badge colours per status. Labels come from the shared `withdrawalStatus` map (spec §3.5). */
const STATUS_CLASS: Record<WithdrawalPaymentStatus, Pick<WithdrawalStatusDisplay, 'className'>> = {
  pending: {
    className: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  processing: {
    className: 'border-sky-200 bg-sky-50 text-sky-800',
  },
  approved: {
    className: 'border-blue-200 bg-blue-50 text-blue-800',
  },
  awaiting_admin: {
    className: 'border-orange-200 bg-orange-50 text-orange-800',
  },
  retry_scheduled: {
    className: 'border-violet-200 bg-violet-50 text-violet-800',
  },
  completed: {
    className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  },
  failed: {
    className: 'border-rose-200 bg-rose-50 text-rose-800',
  },
  rejected: {
    className: 'border-rose-200 bg-rose-50 text-rose-800',
  },
  cancelled: {
    className: 'border-stone-200 bg-stone-50 text-stone-700',
  },
  expired: {
    className: 'border-stone-200 bg-stone-50 text-stone-700',
  },
  partial: {
    className: 'border-amber-200 bg-amber-50 text-amber-900',
  },
  refunded: {
    className: 'border-slate-200 bg-slate-50 text-slate-800',
  },
};

export function getWithdrawalStatusDisplay(status: string | null | undefined): WithdrawalStatusDisplay {
  const raw = (status || '').trim();
  const known = raw as WithdrawalPaymentStatus;
  if (raw && raw in STATUS_CLASS) {
    return { status: raw, label: withdrawalStatus(raw).label, ...STATUS_CLASS[known] };
  }
  return {
    status: raw || 'unknown',
    label: raw ? withdrawalStatus(raw).label : 'Unknown',
    className: 'border-stone-200 bg-stone-50 text-stone-700',
  };
}
